
import React, { useState, useEffect, useRef } from 'react';

const Historia = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [hoveredGeneration, setHoveredGeneration] = useState(null);
  const containerRef = useRef(null);

  // Efecto de scroll para animaciones y posicionamiento inicial
  useEffect(() => {
    // Scroll suave al inicio cuando el componente se monta
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, 1 - rect.top / window.innerHeight));
        setScrollProgress(progress);
      }
    };

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const generations = [
    {
      id: 'lorenzo',
      name: 'Lorenzo Vaquer',
      period: '1943 - Fundador',
      description: 'Fundó la compañía en San Juan. Las primeras perforaciones fueron realizadas en su propio campo en Soto, Córdoba.',
      color: 'from-amber-400 to-orange-600',
      position: 'top-4 left-4'
    },
    {
      id: 'jaime',
      name: 'Jaime Vaquer',
      period: 'Segunda Generación',
      description: 'Trabajó junto a su padre Lorenzo durante las siguientes décadas, consolidando el legado familiar.',
      color: 'from-blue-400 to-indigo-600',
      position: 'top-4 right-4'
    },
    {
      id: 'gustavo',
      name: 'Gustavo & Claudia',
      period: 'Tercera Generación',
      description: 'Gustavo Vaquer y su esposa Claudia Ávalo dirigen actualmente la empresa.',
      color: 'from-emerald-400 to-teal-600',
      position: 'bottom-4 left-4'
    },
    {
      id: 'angelica',
      name: 'Angélica Vaquer',
      period: 'Cuarta Generación',
      description: 'La hija de Gustavo y Claudia, representando el futuro de la empresa familiar.',
      color: 'from-purple-400 to-pink-600',
      position: 'bottom-4 right-4'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" ref={containerRef}>
      {/* Hero Section con parallax */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Imagen de fondo con efecto parallax - Hero principal */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/assets/historia.jpg')`,
            transform: `translateY(${scrollProgress * 50}px)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        </div>

        {/* Partículas flotantes */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full opacity-30 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Título principal con efecto de escritura */}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <h1 className="text-6xl md:text-8xl font-bold mb-8 bg-gradient-to-r from-white via-gray-300 to-white bg-clip-text text-transparent">
            80 Años
          </h1>
          <h2 className="text-3xl md:text-5xl font-light mb-12 text-gray-300">
            de Historia Familiar
          </h2>
          <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent mx-auto"></div>
        </div>

        {/* Indicador de scroll */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 animate-bounce">
          <div className="flex flex-col items-center">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
            </div>
            <p className="mt-2 text-sm">Desliza para explorar</p>
          </div>
        </div>
      </div>

      {/* Historia narrativa con generaciones interactivas */}
      <div className="relative min-h-screen">
        {/* Fondo con gradiente dinámico */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900"></div>

        {/* Imagen principal con hover effects */}
        <div 
          className="relative h-screen"
          data-section="main-story"
        >
          <div className="absolute inset-0 flex">
            {/* Imagen principal en blanco y negro */}
            <div className="w-full md:w-1/2 relative overflow-hidden">
              <img
                src="/assets/img_1.jpg"
                alt="Equipo de perforación y trabajadores en los años 40"
                className={`w-full h-full object-cover transition-all duration-1000 ${
                  visibleSections.has('main-story') 
                    ? 'scale-100 opacity-100' 
                    : 'scale-110 opacity-40'
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50"></div>
              
              {/* Overlays interactivos para cada generación */}
              {generations.map((gen, index) => (
                <div
                  key={gen.id}
                  className={`absolute ${gen.position} w-24 h-24 cursor-pointer transform transition-all duration-300 ${
                    hoveredGeneration === gen.id ? 'scale-125' : 'scale-100'
                  }`}
                  onMouseEnter={() => setHoveredGeneration(gen.id)}
                  onMouseLeave={() => setHoveredGeneration(null)}
                >
                  <div className={`w-full h-full rounded-full bg-gradient-to-r ${gen.color} opacity-80 flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold text-xs">{index + 1}</span>
                  </div>
                  {hoveredGeneration === gen.id && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-black/90 text-white p-2 rounded text-xs whitespace-nowrap">
                      {gen.period}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contenido de texto */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16">
              <div className={`max-w-lg transform transition-all duration-1000 ${
                visibleSections.has('main-story')
                  ? 'translate-x-0 opacity-100'
                  : 'translate-x-20 opacity-0'
              }`}>
                <h3 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Nuestra Historia
                </h3>
                
                <div className="space-y-6 text-lg leading-relaxed text-gray-300">
                  <p className="text-xl">
                    <span className="text-2xl font-bold text-white">En 1943</span>, la compañía fue fundada por{" "}
                    <span className="text-amber-400 font-semibold">Lorenzo Vaquer</span> en San Juan. Las primeras 
                    perforaciones fueron realizadas en su propio campo en Soto, Córdoba.
                  </p>
                  
                  <p>
                    Durante las siguientes décadas trabajó junto a su hijo{" "}
                    <span className="text-blue-400 font-semibold">Jaime Vaquer</span>, consolidando 
                    el legado familiar y expandiendo las operaciones.
                  </p>
                  
                  <p>
                    En la actualidad la empresa está dirigida por su tercera y cuarta generación: 
                    su nieto <span className="text-emerald-400 font-semibold">Gustavo Vaquer</span> junto 
                    a su esposa <span className="text-emerald-400 font-semibold">Claudia Ávalo</span> y 
                    la hija de ambos <span className="text-purple-400 font-semibold">Angélica Vaquer</span>.
                  </p>
                </div>

                {/* Línea de tiempo visual */}
                <div className="mt-12 space-y-4">
                  <h4 className="text-xl font-semibold text-white mb-4">Línea de Tiempo</h4>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <span className="text-amber-400 font-bold">1943</span>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-amber-400 to-blue-400"></div>
                    <span className="text-blue-400 font-bold">1950s-80s</span>
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400 to-emerald-400"></div>
                    <span className="text-emerald-400 font-bold">Actualidad</span>
                    <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de generaciones detallada */}
        <div className="py-20 px-8">
          <h3 className="text-4xl font-bold text-center mb-16 text-white">
            Cuatro Generaciones de Excelencia
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {generations.map((gen, index) => (
              <div
                key={gen.id}
                data-section={`generation-${index}`}
                className={`transform transition-all duration-700 delay-${index * 200} ${
                  visibleSections.has(`generation-${index}`)
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                }`}
                onMouseEnter={() => setHoveredGeneration(gen.id)}
                onMouseLeave={() => setHoveredGeneration(null)}
              >
                <div className={`bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-lg border border-gray-700 transform transition-all duration-300 ${
                  hoveredGeneration === gen.id ? 'scale-105 shadow-xl border-gray-500' : 'scale-100'
                }`}>
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${gen.color} mb-4 flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">{gen.name}</h4>
                  <p className="text-gray-400 text-sm mb-3">{gen.period}</p>
                  <p className="text-gray-300 leading-relaxed">{gen.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer final con efecto de brillo */}
        <div className="text-center py-16">
          <div className="max-w-2xl mx-auto px-6">
            <p className="text-2xl text-gray-400 leading-relaxed">
              Una historia de <span className="text-white font-bold">dedicación</span>, 
              <span className="text-white font-bold"> innovación</span> y 
              <span className="text-white font-bold"> tradición familiar</span> que continúa escribiéndose.
            </p>
            <div className="mt-8 w-full h-0.5 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Historia;